import { FC, ReactNode } from 'react';

/**
 * Pure Tabs Component
 * Fully controlled, accessible, and styled with Tailwind
 */

interface Tab {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
}

interface TabListProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  variant?: 'underline' | 'pills' | 'bordered';
  className?: string;
}

interface TabsProps extends TabListProps {
  children: ReactNode;
}

export const TabList: FC<TabListProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'underline',
  className = '',
}) => {
  const variantStyles = {
    underline: {
      container: 'border-b border-gray-100',
      tab: 'inline-flex items-center gap-2 px-4 py-2.5 font-bold text-[10px] uppercase tracking-[0.15em] border-b-2 -mb-px',
      active: 'text-indigo-600 border-indigo-600',
      inactive: 'text-slate-400 border-transparent hover:text-slate-600 hover:border-slate-200',
    },
    pills: {
      container: 'flex gap-1 p-1 bg-slate-100/50 rounded-md border border-slate-100',
      tab: 'inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded',
      active: 'bg-white text-indigo-600 shadow-sm border border-slate-200/50',
      inactive: 'text-slate-400 hover:text-slate-600',
    },
    bordered: {
      container: 'flex border border-slate-200 rounded overflow-hidden',
      tab: 'inline-flex items-center gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-wider border-r last:border-r-0 border-slate-100',
      active: 'bg-slate-50 text-indigo-600',
      inactive: 'bg-white text-slate-400 hover:bg-slate-50/50 hover:text-slate-600',
    },
  };

  const styles = variantStyles[variant];

  return (
    <nav className={`flex flex-wrap ${styles.container} ${className}`} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`
            ${styles.tab}
            ${activeTab === tab.id ? styles.active : styles.inactive}
            transition-all duration-200 ease-in-out
          `}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`panel-${tab.id}`}
          tabIndex={activeTab === tab.id ? 0 : -1}
        >
          {tab.icon}
          <span>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
};

export const Tabs: FC<TabsProps> = ({
  tabs,
  activeTab,
  onChange,
  children,
  variant = 'underline',
  className = '',
}) => {
  return (
    <div className={className}>
      <TabList
        tabs={tabs}
        activeTab={activeTab}
        onChange={onChange}
        variant={variant}
      />
      <div className="mt-6" role="tabpanel" id={`panel-${activeTab}`}>
        {children}
      </div>
    </div>
  );
};

/**
 * TabPanel Component (optional, for explicit tab content)
 */
interface TabPanelProps {
  id: string;
  activeTab: string;
  children: ReactNode;
}

export const TabPanel: FC<TabPanelProps> = ({ id, activeTab, children }) => {
  if (id !== activeTab) return null;

  return (
    <div role="tabpanel" id={`panel-${id}`} className="animate-fadeIn">
      {children}
    </div>
  );
};
